import React, { useCallback, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import Loading from 'components/atoms/Loading';
import Notify from 'components/organisms/Notify';
import { useAppDispatch } from 'store/hooks';
import { loginSSOAction } from 'store/user';
import { LOCAL_STORAGE } from 'utils/constants';
import { getRedirectSSOParam } from 'utils/ssoHelper';

const LoginSso: React.FC = () => {
  const { search } = useLocation();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [error, setError] = useState<any>();
  const [isSuccess, setIsSuccess] = useState(false);
  const currentPage = localStorage.getItem(LOCAL_STORAGE.CURRENT_PAGE);
  const isOpenTrading = localStorage.getItem(LOCAL_STORAGE.IS_OPEN_TRADING);

  const login = useCallback(
    async (code: string, timeout?: string, language?: string) => {
      try {
        setError(undefined);
        if (!executeRecaptcha) {
          return;
        }
        const grecaptchaToken = await executeRecaptcha('submit');

        await dispatch(
          loginSSOAction({
            code,
            redirect_uri: getRedirectSSOParam(timeout, language),
            grecaptcha_token: grecaptchaToken,
          }),
        )
          .unwrap()
          .then(() => setIsSuccess(true))
          .catch((err) => setError(err));
      } catch (err) {
        setError(err);
      }
    },
    [dispatch, executeRecaptcha],
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const code = queryParams.get('code');
    const timeout = queryParams.get('TimeOut');
    const language = queryParams.get('Language');
    if (!code || !timeout || !language) {
      return;
    }

    login(code, timeout, language);
  }, [login, search]);

  if (isSuccess) {
    return (
      <Notify
        isOpen
        type="success"
        title={t('notify.notification')}
        message={t('notify.logged_in_successfully')}
        btnText={t('notify.come_back')}
        handleClose={() => {
          if (isOpenTrading) {
            window.open(process.env.REACT_APP_WEB_TRADING_URL, '_blank');
            localStorage.removeItem(LOCAL_STORAGE.IS_OPEN_TRADING);
          }
          history.push(currentPage || '/');
          localStorage.removeItem(LOCAL_STORAGE.CURRENT_PAGE);
        }}
      />
    );
  }

  if (error) {
    return (
      <Notify
        isOpen
        type="error"
        title={t('notify.notification')}
        message={t('notify.error_has_occurred_please_try_again_later')}
        btnText={t('notify.come_back')}
        handleClose={() => {
          history.push(currentPage || '/');
          localStorage.removeItem(LOCAL_STORAGE.CURRENT_PAGE);
        }}
      />
    );
  }

  return <Loading variant="fullScreen" isShow />;
};

export default LoginSso;
